import { makeObservable, observable, action } from 'mobx';
import env from '../../configs/config';
import $ from 'jquery';
import LocalStorageStore from './localStorageStore';
import SessionTracker from '../../utils/sessionTracker';
import AuthHeaders from '../../utils/ajaxAuthHeaders';
import { toast } from 'react-toastify';
import identityService from '../../services/identityService';
import passkeyService from '../../services/passkeyService';

/*eslint no-use-before-define: 0*/

const ALLOWED_ROLES = ['admin', 'superadmin'];

class ApplicationStore {
  @observable loading = false;
  @observable accessToken = null;
  @observable xeroAccessToken = null;
  @observable defaultModule = null;
  @observable currentModule = null;
  @observable settingKey = {};

  constructor() {
    makeObservable(this);
  }

  @action setDefaultModule = (module) => {
    this.defaultModule = module;
  };

  @action getDefaultModules = () => {
    return LocalStorageStore.defaultModule;
  };

  @action getFullname = () => {
    return LocalStorageStore.getFullname();
  };

  @action getUserId = () => {
    return LocalStorageStore.getUserId();
  };

  @action getUserName = () => {
    return LocalStorageStore.getUserName();
  };

  @action getRoles = () => {
    return LocalStorageStore.getRoles();
  };

  @action logout = () => {
    if (LocalStorageStore.isTokenExists()) {
      $(document).trigger('application-user-logout');
    }
    localStorage.removeItem('userName');
    localStorage.removeItem('ExternalLogin');
    localStorage.removeItem('LoginProvider');
    return LocalStorageStore.removeToken();
  };

  @action isLoggedIn = () => {
    var exp = LocalStorageStore.getExpiryTime();
    var cur = Math.round(new Date().getTime()) / 1000;
    var sessionValid = exp > cur;

    if (!sessionValid) {
      this.logout();
    }
    return sessionValid;
  };

  @action isExternalLoggedIn = () => {
    return JSON.parse(localStorage.getItem('ExternalLogin'));
  };

  // ─── Identity API methods ──────────────────────────────────────────────────

  @action loginWithIdentity = async (email, password, rememberMe) => {
    return identityService.loginWithEmail({ email, password, rememberMe });
  };

  @action verifyIdentityMfa = async (userId, code) => {
    return identityService.verifyLoginMfa(userId, code);
  };

  @action loginWithPhone = async (phoneNumber) => {
    return identityService.loginWithPhone(phoneNumber);
  };

  @action verifyPhoneIdentityMfa = async (userId, code) => {
    return identityService.verifyPhoneLoginMfa(userId, code);
  };

  @action resendIdentityMfa = async (userId) => {
    return identityService.resendLoginMfa(userId);
  };

  @action completeIdentityLogin = async (authResponse, rememberMe = false) => {
    // Validates the auth response and checks role access in memory.
    // Does NOT write to localStorage — that happens only after the user
    // selects an application and reloginWithApplication succeeds.
    const token = authResponse?.token || authResponse?.Token;
    if (!token) {
      throw new Error('Authentication token was not returned.');
    }

    // Temporarily store so getRoles() / checkRoleAccess() can read the token payload.
    LocalStorageStore.saveToken({
      token,
      refreshToken: authResponse?.refreshToken || authResponse?.RefreshToken || authResponse?.refresh_token,
      metadata: authResponse?.metadata || authResponse?.Metadata,
      profile: null,
      rememberMe: !!rememberMe,
    });

    SessionTracker.isExpired = false;
    SessionTracker.isSoonToExpire = false;
    return null;
  };

  @action completeMicrosoftLogin = async (msAccessToken, _msRefreshToken) => {
    if (!msAccessToken) {
      throw new Error('Microsoft access token was not returned.');
    }

    // Store temporarily IN MEMORY (not in localStorage yet)
    // This prevents isLoggedIn() from returning true before application selection
    const authResponse = {
      token: msAccessToken,
      refreshToken: _msRefreshToken || '',
      metadata: null,
    };

    SessionTracker.isExpired = false;
    SessionTracker.isSoonToExpire = false;

    // Return the response so the caller can parse the applications list and roles from the token
    return authResponse;
  };

  @action reloginWithApplication = async (applicationId) => {
    const slot = LocalStorageStore.getStorageSlot();
    const currentToken = slot?.t;

    if (!currentToken) throw new Error('No active session found. Please sign in again.');

    const response = await identityService.relogin(currentToken, applicationId);
    const newToken = response?.token || response?.Token;

    if (!newToken) throw new Error('Failed to obtain credentials for the selected application.');

    // Fetch profile using the new application-scoped token
    let profile = null;
    try {
      profile = await identityService.getUserProfile(newToken);
    } catch (e) {
      profile = null;
    }

    // Only replace t (token) and p (profile).
    // r (refresh token) and m (metadata) are preserved from the existing slot.
    LocalStorageStore.updateTokenAndProfile(newToken, profile);

    localStorage.setItem('ApplicationId', applicationId);
  };

  @action loginWithPasskey = async (email) => {
    return passkeyService.authenticateWithEmail(email);
  };

  @action resetConnection = async () => {
    const slot = LocalStorageStore.getStorageSlot();
    const currentToken = slot?.t;
    const refreshToken = slot?.r;

    if (!refreshToken) {
      throw new Error('No refresh token available. Please sign in again.');
    }

    const data = await identityService.refreshToken({ token: currentToken, refreshToken });
    const newToken = data?.token || data?.Token;
    const newRefreshToken = data?.refreshToken || data?.RefreshToken;

    if (!newToken) {
      throw new Error('Token refresh did not return a new token.');
    }

    LocalStorageStore.saveToken({
      token: newToken,
      refreshToken: newRefreshToken || refreshToken,
      profile: slot?.p || null,
      metadata: slot?.m || null,
      rememberMe: slot?.rm !== false,
    });

    await this.ClearTokens();
  };

  checkRoleAccess = () => {
    const roles = this.getRoles();
    return roles.some((r) => ALLOWED_ROLES.includes((r || '').toLowerCase().replace(/\s/g, '')));
  };

  // ─── Legacy STS methods (kept for existing finance features) ───────────────

  @action GetSigninUrl(applicationId, env) {
    const defaultUrl = env['STS_SIGNIN_URL'];
    const applicationIds = [
      '7E84E687-8B74-465F-87F4-835553B75E17',
      'D53F6593-2025-4DE3-BB6B-FD6DDE8FC774',
      '42F1B2CA-3B49-46BF-8FB9-93AE589B20A7'
    ];

    localStorage.setItem('ApplicationId', applicationId);

    if (applicationIds.includes(applicationId)) {
      return "https://viz-security-token-service-dev.azurewebsites.net/connect/token";
    }

    return defaultUrl;
  }

  @action GetAPIUrl(env) {
    const defaultUrl = env['WEBAPI_BASE_URL'];
    const applicationIds = [
      '7E84E687-8B74-465F-87F4-835553B75E17',
      'D53F6593-2025-4DE3-BB6B-FD6DDE8FC774',
      '42F1B2CA-3B49-46BF-8FB9-93AE589B20A7'
    ];

    const applicationId = localStorage.getItem('ApplicationId');

    if (applicationIds.includes(applicationId)) {
      return "https://vizybility-api-dev.azurewebsites.net";
    }

    return defaultUrl;
  }

  @action login = (userName, password, ApplicationId) => {
    localStorage.setItem('userName', userName);

    const url = this.GetSigninUrl(ApplicationId, env);

    var settings = {
      async: true,
      crossDomain: true,
      url: url,
      method: 'POST',
      headers: {
        'cache-control': 'no-cache',
        'Postman-Token': '57e2d224-e409-47a0-93c0-e45a0c8e34e2',
      },
      data: {
        grant_type: 'password',
        username: userName,
        password: password,
        client_id: env['STS_CLIENT_ID'],
        client_secret: env['STS_CLIENT_SECRET'],
        scope: env['STS_SCOPE'],
        ApplicationId: ApplicationId,
      },
    };

    return $.ajax(settings).done(function (data) {
      LocalStorageStore.saveToken(data);
      SessionTracker.isExpired = false;
      SessionTracker.isSoonToExpire = false;
    });
  };

  @action signInWithAD = (data, accessToken, ApplicationId) => {
    localStorage.setItem('userName', data.userPrincipalName);

    const url = this.GetSigninUrl(ApplicationId, env);

    var settings = {
      async: true,
      crossDomain: true,
      url: url,
      method: 'POST',
      headers: {
        'cache-control': 'no-cache',
        'Postman-Token': '57e2d224-e409-47a0-93c0-e45a0c8e34e2',
      },
      data: {
        grant_type: 'password',
        username: data.userPrincipalName,
        password: data.id,
        client_id: env['STS_CLIENT_ID'],
        client_secret: env['STS_CLIENT_SECRET'],
        scope: env['STS_SCOPE'],
        ApplicationId: ApplicationId,
        ExternalUserId: data.id,
        AccessToken: accessToken,
        IsExternalRequest: true,
      },
    };
    return $.ajax(settings).done(function (data) {
      LocalStorageStore.saveToken(data);
      localStorage.setItem('ExternalLogin', true);
      SessionTracker.isExpired = false;
      SessionTracker.isSoonToExpire = false;
    });
  };

  // ─── Finance methods (unchanged) ──────────────────────────────────────────

  @action async connectDB() {
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/GetQBRedirectUrl',
      dataType: 'json',
      headers: new AuthHeaders().getToken(),
      data: {},
      context: this,
    })
      .done(async function (data) {
        window.location.href = data.redirectUrl;
      })
      .fail(function (error) {})
      .always(function () {});
  }

  @action async GetQBAccessToken() {
    this.loading = true;
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/QbCreateAccessToken',
      dataType: 'json',
      headers: new AuthHeaders().getToken(),
      data: {},
      context: this,
    })
      .done(async function (data) {
        this.loading = false;

        if (data && data.AccessToken && data.AccessToken !== '') {
          this.accessToken = true;
        } else {
          this.accessToken = false;
        }
      })
      .fail(function (error) {
        this.accessToken = false;
        toast.error(error.responseText);
      })
      .always(function () {
        this.loading = false;
      });
  }

  @action async GetXeroToken() {
    this.loading = true;
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/XeroCreateAccessToken',
      dataType: 'json',
      headers: new AuthHeaders().getToken(),
      data: {},
      context: this,
    })
      .done(async function (data) {
        this.loading = false;

        if (data && data.AccessToken && data.AccessToken !== '') {
          this.xeroAccessToken = true;
        } else {
          this.xeroAccessToken = false;
        }
      })
      .fail(function (error) {
        this.xeroAccessToken = false;
        toast.error(error.responseText);
      })
      .always(function () {
        this.loading = false;
      });
  }

  @action async connectXero() {
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/GetXeroRedirectUrl',
      dataType: 'json',
      headers: new AuthHeaders().getToken(),
      data: {},
      context: this,
    })
      .done(async function (data) {
        window.location.href = data.redirectUrl;
      })
      .fail(function (error) {})
      .always(function () {});
  }

  @action async ClearTokens() {
    this.loading = true;
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/ClearTokens',
      dataType: 'json',
      headers: new AuthHeaders().getToken(),
      data: {},
      context: this,
    })
      .done(async function (data) {
        if (this.currentModule?.includes('QuickBook')) {
          this.GetQBAccessToken();
        }
        if (this.currentModule?.includes('Xero')) {
          this.GetXeroToken();
        }
        this.GetCurrentModule();
        this.loading = false;
      })
      .fail(function (error) {
        toast.error(error.responseText);
      })
      .always(function () {
        this.loading = false;
      });
  }

  @action async GetSettingByKey(key) {
    this.loading = true;
    let url = this.GetAPIUrl(env);
    return $.ajax({
      url: url + '/Settings/GetSettingByKey?key=' + key,
      dataType: 'json',
      headers: new AuthHeaders().getToken(),
      data: {},
      context: this,
    })
      .done(async function (data) {
        this.loading = false;
        this.settingKey = data;
      })
      .fail(function (error) {
        toast.error(error);
      })
      .always(function () {
        this.loading = false;
      });
  }

  @action async GetCurrentModule() {
    this.loading = true;
    const currentModule = await this.GetSettingByKey('FinanceServiceName');
    this.currentModule = currentModule.SettingValue;
  }

  @action async SyncXeroInvoice() {
    this.loading = true;
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/SyncXeroInvoices',
      dataType: 'json',
      type: 'POST',
      headers: new AuthHeaders().getToken(),
      contentType: 'application/json',
      context: this,
    })
      .done(function (data) {
        toast.success('Invoice Sync successfully');
      })
      .fail(function (error) {
        toast.error(error.responseText);
      })
      .always(function () {
        this.loading = false;
      });
  }

  @action async SyncXeroContact() {
    this.loading = true;
    let url = this.GetAPIUrl(env);

    return $.ajax({
      url: url + '/Finance/SyncXeroContacts',
      dataType: 'json',
      type: 'POST',
      headers: new AuthHeaders().getToken(),
      contentType: 'application/json',
      context: this,
    })
      .done(function (data) {
        toast.success('Contact Sync successfully');
      })
      .fail(function (error) {
        toast.error(error.responseText);
      })
      .always(function () {
        this.loading = false;
      });
  }
}

const applicationStore = new ApplicationStore();

export default applicationStore;
